import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight } from 'react-icons/fi';

function BlogArticleShell({ sections, relatedBlogs, children }) {
  const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);
  const [activeSectionId, setActiveSectionId] = useState(sectionIds[0] ?? '');
  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const tocItemRefs = useRef({});

  useEffect(() => {
    const handleLeftScroll = () => {
      if (leftSidebarRef.current && rightSidebarRef.current) {
        rightSidebarRef.current.scrollTop = leftSidebarRef.current.scrollTop;
      }
    };

    const handleRightScroll = () => {
      if (leftSidebarRef.current && rightSidebarRef.current) {
        leftSidebarRef.current.scrollTop = rightSidebarRef.current.scrollTop;
      }
    };

    const leftEl = leftSidebarRef.current;
    const rightEl = rightSidebarRef.current;

    if (leftEl) leftEl.addEventListener('scroll', handleLeftScroll);
    if (rightEl) rightEl.addEventListener('scroll', handleRightScroll);

    return () => {
      if (leftEl) leftEl.removeEventListener('scroll', handleLeftScroll);
      if (rightEl) rightEl.removeEventListener('scroll', handleRightScroll);
    };
  }, []);

  useEffect(() => {
    if (!sectionIds.length) {
      return undefined;
    }

    const handleVisibilityChange = (entries) => {
      const visibleEntries = entries.filter((entry) => entry.isIntersecting);

      if (!visibleEntries.length) {
        return;
      }

      visibleEntries.sort((left, right) => right.intersectionRatio - left.intersectionRatio);
      setActiveSectionId(visibleEntries[0].target.id);
    };

    const observer = new IntersectionObserver(handleVisibilityChange, {
      root: null,
      rootMargin: '-18% 0px -62% 0px',
      threshold: [0.08, 0.16, 0.24, 0.32],
    });

    sectionIds.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) {
        observer.observe(element);
      }
    });

    const hash = window.location.hash.replace('#', '');
    if (hash && sectionIds.includes(hash)) {
      setActiveSectionId(hash);
    }

    return () => observer.disconnect();
  }, [sectionIds]);

  useEffect(() => {
    const activeTocItem = tocItemRefs.current[activeSectionId];
    if (!activeTocItem) {
      return;
    }

    activeTocItem.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }, [activeSectionId]);

  return (
    <main className="mx-auto w-full max-w-[96rem] px-3 pb-20 pt-12 sm:px-5 sm:pt-16 lg:px-8 lg:pt-16">
      <div className="grid gap-12 lg:grid-cols-[15rem_minmax(0,1fr)_17rem] lg:items-start xl:gap-16">
        <aside className="hidden self-start lg:sticky lg:top-24 lg:block">
          <div ref={leftSidebarRef} className="max-h-[calc(100vh-4rem)] overflow-y-auto rounded-3xl border border-slate-200/90 bg-white/90 p-4 shadow-[0_20px_70px_-30px_rgba(15,23,42,0.28)] backdrop-blur dark:border-white/10 dark:bg-[#0b1324]/90 dark:shadow-[0_20px_70px_-35px_rgba(0,0,0,0.55)] blog-rail-scrollbar">
            <nav className="space-y-2">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  ref={(element) => {
                    if (element) {
                      tocItemRefs.current[section.id] = element;
                    } else {
                      delete tocItemRefs.current[section.id];
                    }
                  }}
                  href={`#${section.id}`}
                  aria-current={activeSectionId === section.id ? 'true' : undefined}
                  className={`group flex items-start gap-3 rounded-2xl border px-3 py-3 transition ${
                    activeSectionId === section.id
                      ? 'border-sky-200 bg-sky-50 text-slate-900 shadow-sm dark:border-sky-400/25 dark:bg-sky-400/10 dark:text-white'
                      : 'border-transparent hover:border-sky-200 hover:bg-sky-50 dark:hover:border-sky-400/20 dark:hover:bg-sky-400/5'
                  }`}
                >
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition ${
                    activeSectionId === section.id
                      ? 'bg-sky-100 text-sky-700 dark:bg-sky-400/20 dark:text-sky-200'
                      : 'bg-slate-100 text-slate-700 dark:bg-white/[0.04] dark:text-slate-200'
                  }`}>
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="min-w-0">
                    <span className={`block text-sm font-semibold leading-5 ${activeSectionId === section.id ? 'text-slate-900 dark:text-white' : 'text-slate-900 dark:text-slate-100'}`}>
                      {section.title}
                    </span>
                  </span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        <div className="min-w-0 lg:mx-auto lg:w-full lg:max-w-4xl">{children}</div>

        <aside className="hidden self-start lg:sticky lg:top-24 lg:block">
          <div ref={rightSidebarRef} className="max-h-[calc(100vh-4rem)] overflow-y-auto rounded-3xl border border-slate-200/90 bg-white/90 p-4 shadow-[0_20px_70px_-30px_rgba(15,23,42,0.28)] backdrop-blur dark:border-white/10 dark:bg-[#0b1324]/90 dark:shadow-[0_20px_70px_-35px_rgba(0,0,0,0.55)] blog-rail-scrollbar">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Explore other blogs</h2>

            <div className="mt-5 space-y-3">
              {relatedBlogs.map((blog) => {
                const preview = (
                  <div className="flex items-center gap-3">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-slate-200/80 ${blog.accentClass} dark:border-white/10`}>
                      {blog.logoSrc ? (
                        <img src={blog.logoSrc} alt={blog.logoAlt} className="h-7 w-7" />
                      ) : (
                        <span className="text-xs font-bold uppercase tracking-[0.14em]">{blog.logoLabel}</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="mt-1 text-sm font-semibold leading-5 text-slate-900 dark:text-slate-100">
                        {blog.title}
                      </p>
                    </div>

                    <FiArrowRight className="shrink-0 text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-sky-600 dark:text-slate-500 dark:group-hover:text-sky-300" size={16} />
                  </div>
                );

                if (blog.href) {
                  return (
                    <Link
                      key={blog.slug}
                      to={blog.href}
                      className="group block rounded-2xl border border-slate-200 bg-slate-50/70 px-3 py-3 transition hover:border-sky-300 hover:bg-sky-50/80 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-sky-400/30 dark:hover:bg-sky-400/5"
                    >
                      {preview}
                    </Link>
                  );
                }

                return (
                  <div
                    key={blog.slug}
                    className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-3 py-3 dark:border-white/15 dark:bg-white/[0.02]"
                  >
                    {preview}
                  </div>
                );
              })}
            </div>

          </div>
        </aside>
      </div>
    </main>
  );
}

export default BlogArticleShell;
